// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    content: AutocompleteItemModel[] | null;
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteContentStyles;
}

export interface AutocompleteContentStyles {
    headerText?: TypographyPresentationProps;
    link?: LinkPresentationProps;
    wrapper?: InjectableCss;
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
}

export const autocompleteContentStyles: AutocompleteContentStyles = {
    headerText: {
        size: fontConfig.fontSize.small,
        weight: fontConfig.weight.semiBold,
        lineHeight: "17px",
        color: "text.main",
        transform: "uppercase",
        css: css`
            letter-spacing: 1px;
            text-align: left;
        `,
    },
    link: {
        typographyProps: {
            size: fontConfig.fontSize.small,
            weight: fontConfig.weight.light,
            lineHeight: "20px",
            color: "common.ui1",
            css: css`
                letter-spacing: 0.2px;
                display: block;
                width: 100%;
                text-align: left;
                &&& {
                    color: ${getColor("common.ui1")};
                    white-space: pre-wrap;
                }
                b {
                    color: ${getColor("text.main")};
                }
            `,
            ellipsis: true,
        },
    },
    wrapper: {
        css: css`
            margin-bottom: 40px;
            display: flex;
            flex-direction: column;
        `,
    },
    leftColumn: {
        css: css`
            width: 78px;
            margin-right: 35px;
            margin-bottom: 6px;
        `,
    },
    rightColumn: {
        css: css`
            width: calc(100% - 113px);
        `,
    },
};

const styles = autocompleteContentStyles;

class AutocompleteContent extends React.Component<Props> {
    private readonly styles: AutocompleteContentStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { content } = this.props;
        if (!content || content.length === 0) {
            return null;
        }

        const styles = this.styles;
        return (
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.leftColumn}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteContent_Header"
                        className="autocomplete-content-header"
                    >
                        {translate("Content")}
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.rightColumn}>
                    <GridContainer gap={5}>
                        {content.map((content, index) => (
                            <GridItem
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                width={12}
                            >
                                <Link
                                    {...styles.link}
                                    onClick={() => {
                                        this.props.goToUrl(content.url);
                                    }}
                                    data-test-selector="autocompleteContent_Result"
                                    className="autocomplete-content-link"
                                >
                                    {content.displayTitle}
                                </Link>
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            </StyledWrapper>
        );
    }
}

export default AutocompleteContent;
